import { orderSourceLabel } from '@/utils/orderSource';
/**
 * IncomingView - Quarantine queue for automated orders (Stripe, email parser, etc.)
 * Full-height layout: order list on left, detail viewer on right.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X, Inbox, FileText, AlertCircle, Trash2, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/config/constants';
import { formatDeliveryDateDisplay } from '@/utils/dateUtils';
import { formatPhoneNumberDisplay } from '@/utils/phoneUtils';
import useOrderSheetPrint from '@/hooks/useOrderSheetPrint';
import AuthenticatedDocumentLink from '@/components/AuthenticatedDocumentLink';
import { useIncomingOrders } from '@/context/IncomingOrdersContext';

const WARNING_LABELS = {
  missing_delivery_date: 'Missing delivery date',
  missing_internal_order_number: 'Internal order number not assigned',
  missing_product_description: 'Missing product description',
  line_items_fetch_failed: 'Stripe line items could not be fetched',
  ai_extraction_failed: 'AI extraction failed during Stripe intake',
  source_document_storage_failed: 'Source snapshot could not be stored',
};

const formatWarningLabel = (warning) => WARNING_LABELS[warning] || warning.replace(/_/g, ' ');

const IncomingContent = () => {
  const {
    orders,
    status,
    decisions,
    decisionStatus,
    ownerKey,
  } = useIncomingOrders();
  const [viewingOrder, setViewingOrder] = useState(null);
  const openOrderSheet = useOrderSheetPrint({
    order: viewingOrder,
    canPrint: Boolean(viewingOrder && orders.some(order => order.id === viewingOrder.id)),
  });
  const [rejectTarget, setRejectTarget] = useState(null);

  // Clear viewer if the viewed order disappears
  useEffect(() => {
    if (viewingOrder && !orders.find(o => o.id === viewingOrder.id)) {
      setViewingOrder(null);
    }
  }, [orders, viewingOrder]);

  const screen = useMemo(() => decisions.createScreen(ownerKey), [decisions, ownerKey]);
  const opening = useRef(null);
  const [feedback, setFeedback] = useState('');
  useLayoutEffect(() => {
    screen.active = true;
    return () => { screen.active = false; opening.current = null; };
  }, [screen]);
  const blocked = !decisionStatus.writable;
  const closeReject = () => { opening.current = null; setRejectTarget(null); };
  const openReject = order => {
    if (blocked) return;
    opening.current = { id: order.id };
    setRejectTarget(order);
  };
  useEffect(() => {
    if (rejectTarget && !orders.some(order => order.id === rejectTarget.id)) {
      opening.current = null;
      setRejectTarget(null);
    }
  }, [orders, rejectTarget]);

  const run = async (kind, ids, batch = false) => {
    const confirmedOpening = kind === 'reject' ? opening.current : null;
    const result = await decisions.run(kind, ids, screen);
    if (!screen.active || result.state === 'retired') return;
    if (kind === 'reject' && opening.current !== confirmedOpening) return;
    if (result.state === 'blocked') {
      setFeedback('This decision is no longer available. Refresh and review the current queue.');
      return;
    }
    const complete = !result.refused && !result.unknown && !result.unattempted;
    const message = batch
      ? `Approved ${result.approved}; refused ${result.refused}; uncertain ${result.unknown}; not attempted ${result.unattempted}.`
      : result.unknown ? 'The decision was not confirmed and may have taken effect. Refresh and review before another action.'
        : result.refused ? 'The decision was refused. Refresh and review the order and your access.'
          : result.absent ? 'The order was already absent. No new rejection is confirmed.'
            : kind === 'approve' ? 'Order approved' : 'Order rejected';
    setFeedback(message);
    if (complete && (result.approved || result.rejected)) toast.success(message);
    else toast.error(message);
    if (kind === 'reject' && complete) closeReject();
  };
  const handleApprove = id => run('approve', [id]);
  const handleReject = () => {
    if (!rejectTarget || opening.current?.id !== rejectTarget.id) return;
    if (!orders.some(order => order.id === rejectTarget.id)) { closeReject(); return; }
    return run('reject', [rejectTarget.id]);
  };
  const handleApproveAll = () => run('approve', orders.map(order => order.id), true);

  return (
    <div className="h-[calc(100vh-48px)] flex flex-col" data-testid="incoming-view">

      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-stone-200 flex-shrink-0">
        <Inbox className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-stone-800">Incoming Orders</span>
        <span className="text-xs text-stone-500">{orders.length} awaiting review</span>
        {orders.length > 1 && (
          <Button
            size="sm"
            onClick={handleApproveAll}
            disabled={blocked}
            className="ml-auto h-7 text-xs gap-1 bg-green-600 hover:bg-green-700"
            data-testid="approve-all-btn"
          >
            <Check className="h-3.5 w-3.5" />
            Approve All ({orders.length})
          </Button>
        )}
      </div>

      <div className="px-4 py-2 space-y-2 text-sm" aria-live="polite">
        {decisionStatus.pending && <p>A decision is pending. Closing this screen does not cancel it.</p>}
        {feedback && <p data-testid="incoming-decision-result">{feedback}</p>}
        {decisionStatus.uncertain && <div role="alert">
          <p>A previous decision may have taken effect. Nothing will be resent automatically.
            A missing order in this limited list does not prove which action changed it.</p>
          {decisionStatus.reviewReady && <Button variant="outline" onClick={() => decisions.acknowledgeUncertainty()}
            data-testid="incoming-acknowledge-uncertainty">
            I have reviewed the queue; allow a deliberate new action
          </Button>}
        </div>}
        <Button size="sm" variant="outline" disabled={decisionStatus.pending || status === 'loading' || status === 'refreshing'}
          onClick={() => decisions.review()} data-testid="incoming-review">Refresh and review</Button>
      </div>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left panel: order list */}
        <div className="w-80 flex-shrink-0 border-r border-stone-200 bg-stone-50 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {status === 'loading' ? (
              <div className="text-sm text-stone-400 text-center py-12">Loading...</div>
            ) : status === 'unavailable' ? (
              <div className="text-center py-16" data-testid="incoming-unavailable">
                <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
                <p className="text-stone-600 font-medium">Incoming orders are temporarily unavailable</p>
                <p className="text-stone-400 text-xs mt-1">The app will retry automatically.</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16">
                <Check className="h-10 w-10 text-green-400 mx-auto mb-3" />
                <p className="text-stone-500 font-medium">No pending orders</p>
                <p className="text-stone-400 text-xs mt-1">New orders from Stripe will appear here</p>
              </div>
            ) : (
              orders.map((order) => (
                <div
                  key={order.id}
                  className={`rounded-lg border p-3 cursor-pointer transition-all ${
                    viewingOrder?.id === order.id
                      ? 'bg-amber-50 border-amber-300 shadow-sm'
                      : 'bg-white hover:bg-stone-50 border-stone-200'
                  }`}
                  onClick={() => setViewingOrder(order)}
                  data-testid={`incoming-card-${order.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-stone-900 truncate">
                        {order.recipient_name || 'Unknown'}
                      </div>
                      <div className="text-[11px] text-stone-400 truncate">
                        {orderSourceLabel(order, 'Unknown source')}
                        {order.delivery_date && <span> · {formatDeliveryDateDisplay(order.delivery_date)}</span>}
                      </div>
                      {order.product_description && (
                        <div className="text-[11px] text-stone-500 truncate mt-0.5">
                          {order.product_description}
                        </div>
                      )}
                      {order.total_price && (
                        <div className="text-[11px] font-semibold text-green-700 mt-0.5">
                          ${order.total_price.toFixed(2)}
                        </div>
                      )}
                      {(order.ingestion_warnings || []).length > 0 && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800"
                          data-testid={`incoming-warning-badge-${order.id}`}
                        >
                          <AlertCircle className="h-3 w-3" />
                          {(order.ingestion_warnings || []).length} warning{(order.ingestion_warnings || []).length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    {order.internal_order_number && (
                      <span className="px-1 bg-primary/10 text-primary text-[10px] font-mono rounded flex-shrink-0">
                        {order.internal_order_number}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleApprove(order.id); }}
                      disabled={blocked}
                      className="h-6 text-[11px] px-2 flex-1 bg-green-600 hover:bg-green-700 gap-1"
                      data-testid={`approve-${order.id}`}
                    >
                      <Check className="h-3 w-3" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); openReject(order); }}
                      disabled={blocked}
                      className="h-6 text-[11px] px-2 text-red-600 border-red-200 hover:bg-red-50 gap-1"
                      data-testid={`reject-${order.id}`}
                    >
                      <X className="h-3 w-3" /> Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right panel: order detail */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white">
          {viewingOrder ? (
            <>
              <div className="flex items-center gap-3 px-5 py-2.5 border-b bg-stone-50 flex-shrink-0">
                <span className="font-semibold text-sm text-stone-800">{viewingOrder.recipient_name}</span>
                {viewingOrder.internal_order_number && (
                  <span className="px-1.5 bg-primary/10 text-primary text-[10px] font-mono rounded">{viewingOrder.internal_order_number}</span>
                )}
                {orderSourceLabel(viewingOrder, '') && <span className="text-xs text-primary">{orderSourceLabel(viewingOrder)}</span>}
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApprove(viewingOrder.id)}
                    disabled={blocked}
                    className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700"
                    data-testid="detail-approve-btn"
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openReject(viewingOrder)}
                    disabled={blocked}
                    className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="detail-reject-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-2 gap-5 text-sm max-w-2xl">
                  <div>
                    <span className="font-medium text-stone-500">Network</span>
                    <p className="text-stone-900">{orderSourceLabel(viewingOrder, 'N/A')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Order #</span>
                    <p className="text-stone-900 font-mono text-xs break-all">{viewingOrder.order_number || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Recipient</span>
                    <p className="text-stone-900">{viewingOrder.recipient_name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Phone</span>
                    <p className="text-stone-900">{formatPhoneNumberDisplay(viewingOrder.recipient_phone) || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Address</span>
                    <p className="text-stone-900">
                      {viewingOrder.recipient_street_address || 'N/A'}
                      {viewingOrder.recipient_street_address_2 && <><br />{viewingOrder.recipient_street_address_2}</>}
                      {(viewingOrder.recipient_city || viewingOrder.recipient_state) && (
                        <><br />{[viewingOrder.recipient_city, viewingOrder.recipient_state, viewingOrder.recipient_zip_code].filter(Boolean).join(', ')}</>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Delivery Date</span>
                    <p className="text-stone-900">{viewingOrder.delivery_date ? formatDeliveryDateDisplay(viewingOrder.delivery_date) : 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Accepted Date</span>
                    <p className="text-stone-900">{viewingOrder.order_accepted_date ? formatDeliveryDateDisplay(viewingOrder.order_accepted_date) : 'N/A'}</p>
                  </div>
                  {(viewingOrder.ingestion_warnings || []).length > 0 && (
                    <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="incoming-warning-list">
                      <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                        <AlertCircle className="h-4 w-4" />
                        Intake Warnings
                      </div>
                      <ul className="list-disc pl-5 space-y-1 text-amber-900">
                        {(viewingOrder.ingestion_warnings || []).map((warning) => (
                          <li key={warning}>{formatWarningLabel(warning)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {viewingOrder.document_id && (
                    <div className="col-span-2">
                      <span className="font-medium text-stone-500">Source Snapshot</span>
                      <p>
                        <AuthenticatedDocumentLink
                          documentKey={JSON.stringify([viewingOrder.id, viewingOrder.document_id, viewingOrder.resolved_source || viewingOrder.network_source, viewingOrder.desktop_sync?.origin])}
                          url={`${API}/orders/${viewingOrder.id}/document?stamp=1`}
                          orderNumber={viewingOrder.internal_order_number || viewingOrder.order_number}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                          data-testid="incoming-view-source-link"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          View stored Stripe source
                        </AuthenticatedDocumentLink>
                      </p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <button
                      type="button"
                      onClick={openOrderSheet}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      data-testid="incoming-print-order"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      Print Order
                    </button>
                  </div>
                  {viewingOrder.card_message && (
                    <div>
                      <span className="font-medium text-stone-500">Card Message</span>
                      <p className="text-stone-900 italic whitespace-pre-line">"{viewingOrder.card_message}"</p>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-stone-500">Total Price</span>
                    <p className="text-stone-900 text-lg font-bold">{viewingOrder.total_price ? `$${viewingOrder.total_price.toFixed(2)}` : 'N/A'}</p>
                  </div>
                  {viewingOrder.product_description && (
                    <div className="col-span-2">
                      <span className="font-medium text-stone-500">Product Description</span>
                      <p className="text-stone-900 font-medium">{viewingOrder.product_description}</p>
                    </div>
                  )}
                  {viewingOrder.special_instructions && (
                    <div className="col-span-2">
                      <span className="font-medium text-stone-500">Special Instructions</span>
                      <p className="text-stone-900">{viewingOrder.special_instructions}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-300">
              <div className="text-center">
                <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select an order to review</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject confirmation dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && closeReject()}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Order?</DialogTitle>
            <DialogDescription>
              This will permanently delete the order for "{rejectTarget?.recipient_name}". This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeReject}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={blocked} data-testid="confirm-reject-btn">
              Reject & Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const IncomingView = () => {
  const { ownerKey } = useIncomingOrders();
  return <IncomingContent key={ownerKey} />;
};

export default IncomingView;
